import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { useController, useForm } from 'react-hook-form';

import { calendarApi } from '../../features/calendar';
import createStore from './store';

import { Spinner } from '../../components/spinner';
import { Label } from '../../components/label';
import { ErrorMessage } from '../../components/error-message';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { ApiUtility } from '../../utilities/api';
import { Alert, AlertType } from '../../components/alert';
import { UmbracoUtility } from '../../utilities/umbraco';
import { StringUtility } from '../../utilities/string';
import { ErrorBoundary } from '../../components/error-boundary';
import { FormUtility } from '../../utilities/form';
import { Button, ButtonType } from '../../components/button';

interface IRegisterChangeAppProps {
    eventId: number;
    index: number;
}

const RegisterChangeWrapper = ({ children }: React.PropsWithChildren<{}>) => (
    <section className='mt-3'>
        <h2>Register Change</h2>
        {children}
    </section>
);

export const RegisterChangeApp = ({ eventId, index }: IRegisterChangeAppProps) => {
    const {
        data: permission,
        isError: isGetPermissionError,
        error: getPermissionError
    } = calendarApi.useGetPermissionOnEventQuery(eventId);
    const getPermissionErrorMessage = React.useMemo(() => {
        return ApiUtility.getFetchErrorMessage(error as FetchBaseQueryError);
    }, [getPermissionError]);

    // Check if the currently logged in user is the person who submitted the event
    const isPrivilegedMember = React.useMemo(() => {
        if (!permission) {
            return false;
        }
        const { isMemberLoggedIn, memberHasPrivilege } = permission;
        if (isMemberLoggedIn && memberHasPrivilege) {
            return true;
        }
        return false;
    }, [permission]);

    const isAdmin = React.useMemo(() => {
        if (!permission) {
            return false;
        }
        const { isUserLoggedIn, isAdmin } = permission;
        if (isUserLoggedIn && isAdmin) {
            return true;
        }
        return false;
    }, [permission]);

    const { handleSubmit, control, formState } = useForm({
        mode: 'onBlur'
    });
    const { field, fieldState } = useController({
        name: 'requestChange',
        control,
        rules: {
            required: true,
            validate: FormUtility.Required
        }
    });
    const [saveRequestChanges, { isSuccess, isError, error }] = calendarApi.useSaveRequestChangesMutation();

    const onSubmit = async (data: { requestChange: string }) => {
        const { requestChange } = data;
        await saveRequestChanges({
            requestChange: StringUtility.trimIfString(requestChange),
            calendarEventId: eventId
        });
    };

    const submitErrorMessage = React.useMemo(() => {
        return ApiUtility.getFetchErrorMessage(error as FetchBaseQueryError);
    }, [error]);

    if (isGetPermissionError) {
        return (
            <div className='mt-3'>
                <Alert type={AlertType.Error}>
                    <div>Something went wrong with get permission information!</div>
                    {getPermissionErrorMessage}
                </Alert>
            </div>
        );
    }

    // Display nothing if the current user is neither the submitter nor the admin
    if (!isPrivilegedMember && !isAdmin) {
        return null;
    }

    if (!isPrivilegedMember && isAdmin) {
        return (
            <RegisterChangeWrapper>
                <Alert type={AlertType.Warning}>
                    To update the event, please go to{' '}
                    <a href={UmbracoUtility.getCMSContentUrl(eventId)} target='_blank'>
                        CMS
                    </a>
                    .
                </Alert>
            </RegisterChangeWrapper>
        );
    }

    if (isSuccess) {
        return (
            <RegisterChangeWrapper>
                <Alert type={AlertType.Success}>The request for change has been submitted!</Alert>
            </RegisterChangeWrapper>
        );
    }

    const changeDescriptionLabel = 'Change description';
    const changeDescriptionInputId = `request-change-description-${index}`;

    return (
        <RegisterChangeWrapper>
            <form onSubmit={handleSubmit(onSubmit)} className='register-change-form'>
                {formState?.isSubmitting ? <Spinner isOverlay={true} /> : ''}
                {isError ? (
                    <Alert type={AlertType.Error}>
                        <div>Something went wrong!</div>
                        {submitErrorMessage}
                    </Alert>
                ) : (
                    ''
                )}
                <div className='form-group'>
                    <Label label={changeDescriptionLabel} labelFor={changeDescriptionInputId} required={true} />
                    <textarea className='form-control' id={changeDescriptionInputId} aria-required='true' {...field}></textarea>
                    <ErrorMessage label={changeDescriptionLabel} error={fieldState.error} />
                </div>

                <div className='d-flex justify-content-center'>
                    <Button type={ButtonType.Submit} disabled={formState?.isSubmitting}>
                        Submit change
                    </Button>
                </div>
            </form>
        </RegisterChangeWrapper>
    );
};

export class RegisterChangeForm {
    constructor(element: HTMLElement, index: number) {
        const store = createStore();
        const eventId = Number(element.dataset.eventId);
        if (!eventId || isNaN(eventId)) {
            console.log(`invalid calendar event id: ${eventId}`);
            return;
        }
        ReactDOM.render(
            <Provider store={store}>
                <ErrorBoundary>
                    <RegisterChangeApp {...{ index, eventId }} />
                </ErrorBoundary>
            </Provider>,
            element
        );
    }
}
